import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc, getDoc, addDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Grid, 
    Avatar,
    Chip,
    Container,
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel,
    Tooltip,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import TextsmsIcon from '@mui/icons-material/Textsms';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
};

const Directory = () => {
    const [responders, setResponders] = useState([]);
    const [filteredResponders, setFilteredResponders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminMode, setAdminMode] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [selectedResponder, setSelectedResponder] = useState(null);
    const [editForm, setEditForm] = useState({
        displayName: '',
        unitNumber: '',
        cellNumber: '',
        certLevel: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [activeTab, setActiveTab] = useState('responders');
    const [importantAddresses, setImportantAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(true);
    const [addressError, setAddressError] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [loadingDoctors, setLoadingDoctors] = useState(true);
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [specialties, setSpecialties] = useState([]);
    const [dispatchers, setDispatchers] = useState([]);
    const [loadingDispatchers, setLoadingDispatchers] = useState(true);
    const [dispatcherSearchTerm, setDispatcherSearchTerm] = useState('');

    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                const user = auth.currentUser;
                if (user) {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists() && userDoc.data().role === 'Admin') {
                        setIsAdmin(true);
                    }
                }
            } catch (error) {
                console.error('Error checking admin status:', error);
            }
        };

        checkAdminStatus();
    }, []);

    useEffect(() => {
        const fetchResponders = async () => {
            try {
                const respondersSnapshot = await getDocs(collection(db, 'users'));
                const allUsers = respondersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                const nonDispatcherUsers = allUsers.filter(user => user.certLevel?.toLowerCase() !== 'dispatcher');
                // Sort responders by unit number
                const sortedResponders = nonDispatcherUsers.sort((a, b) => {
                    const aUnit = a.unitNumber || '';
                    const bUnit = b.unitNumber || '';
                    return aUnit.localeCompare(bUnit, undefined, { numeric: true });
                });
                setResponders(sortedResponders);
                setFilteredResponders(sortedResponders);
            } catch (error) {
                console.error('Error fetching responder data:', error);
            }
        };

        fetchResponders();
    }, []);

    useEffect(() => {
        const fetchImportantAddresses = async () => {
            try {
                setLoadingAddresses(true);
                const addressesSnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'ImportantAddresses'));
                const addressesList = addressesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setImportantAddresses(addressesList);
            } catch (error) {
                console.error('Error fetching important addresses:', error);
                setAddressError('Failed to fetch addresses.');
            } finally {
                setLoadingAddresses(false);
            }
        };

        fetchImportantAddresses();
    }, []);

    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                setLoadingDoctors(true);
                const doctorsSnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'Doctors'));
                const doctorsList = doctorsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setDoctors(doctorsList);

                // Extract unique specialties
                const uniqueSpecialties = [...new Set(doctorsList.map(doctor => doctor.Specialty))];
                setSpecialties(uniqueSpecialties);
            } catch (error) {
                console.error('Error fetching doctors:', error);
            } finally {
                setLoadingDoctors(false);
            }
        };

        fetchDoctors();
    }, []);

    useEffect(() => {
        const fetchDispatchers = async () => {
            try {
                setLoadingDispatchers(true);
                const respondersSnapshot = await getDocs(collection(db, 'users'));
                const dispatchersList = respondersSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                    }))
                    .filter(user => user.certLevel?.toLowerCase() === 'dispatcher');
                setDispatchers(dispatchersList);
            } catch (error) {
                console.error('Error fetching dispatchers:', error);
            } finally {
                setLoadingDispatchers(false);
            }
        };

        fetchDispatchers();
    }, []);

    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);
        const filtered = responders.filter(responder =>
            responder.displayName?.toLowerCase().includes(value) ||
            responder.unitNumber?.toLowerCase().includes(value) ||
            responder.cellNumber?.includes(value)
        );
        // Sort filtered results by unit number
        const sortedFiltered = filtered.sort((a, b) => {
            const aUnit = a.unitNumber || '';
            const bUnit = b.unitNumber || '';
            return aUnit.localeCompare(bUnit, undefined, { numeric: true });
        });
        setFilteredResponders(sortedFiltered);
    };

    const handleOpenEditDialog = (responder) => {
        setSelectedResponder(responder);
        setEditForm({
            displayName: responder.displayName || '',
            unitNumber: responder.unitNumber || '',
            cellNumber: responder.cellNumber || '',
            certLevel: responder.certLevel || ''
        });
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setSelectedResponder(null);
        setEditForm({
            displayName: '',
            unitNumber: '',
            cellNumber: '',
            certLevel: ''
        });
    };

    const handleEditSubmit = async () => {
        try {
            const responderRef = doc(db, 'users', selectedResponder.id);
            await updateDoc(responderRef, {
                displayName: editForm.displayName,
                unitNumber: editForm.unitNumber,
                cellNumber: editForm.cellNumber,
                certLevel: editForm.certLevel
            });

            // Update local state
            const updatedResponders = responders.map(responder => 
                responder.id === selectedResponder.id 
                    ? { ...responder, ...editForm }
                    : responder
            );
            setResponders(updatedResponders);
            setFilteredResponders(updatedResponders);

            setSnackbar({
                open: true,
                message: 'Responder updated successfully',
                severity: 'success'
            });
            handleCloseEditDialog();
        } catch (error) {
            console.error('Error updating responder:', error);
            setSnackbar({
                open: true,
                message: 'Error updating responder',
                severity: 'error'
            });
        }
    };

    const handleDelete = async (responderId) => {
        if (window.confirm('Are you sure you want to delete this responder?')) {
            try {
                await deleteDoc(doc(db, 'users', responderId));
                const updatedResponders = responders.filter(r => r.id !== responderId);
                setResponders(updatedResponders);
                setFilteredResponders(updatedResponders);
                setSnackbar({
                    open: true,
                    message: 'Responder deleted successfully',
                    severity: 'success'
                });
            } catch (error) {
                console.error('Error deleting responder:', error);
                setSnackbar({
                    open: true,
                    message: 'Error deleting responder',
                    severity: 'error'
                });
            }
        }
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setEditForm({
            displayName: '',
            unitNumber: '',
            cellNumber: '',
            certLevel: ''
        });
    };

    const handleAddSubmit = async () => {
        try {
            const newResponder = {
                displayName: editForm.displayName,
                unitNumber: editForm.unitNumber,
                cellNumber: editForm.cellNumber,
                certLevel: editForm.certLevel,
                createdAt: new Date().toISOString()
            };

            const docRef = await addDoc(collection(db, 'users'), newResponder);
            setResponders([...responders, { id: docRef.id, ...newResponder }]);
            setFilteredResponders([...filteredResponders, { id: docRef.id, ...newResponder }]);
            
            setSnackbar({
                open: true,
                message: 'Responder added successfully',
                severity: 'success'
            });
            handleCloseAddDialog();
        } catch (error) {
            console.error('Error adding responder:', error);
            setSnackbar({
                open: true,
                message: 'Error adding responder',
                severity: 'error'
            });
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleDispatcherSearch = (event) => {
        setDispatcherSearchTerm(event.target.value.toLowerCase());
    };

    const filteredDispatchers = dispatchers.filter(dispatcher =>
        dispatcher.displayName?.toLowerCase().includes(dispatcherSearchTerm) ||
        dispatcher.unitNumber?.toLowerCase().includes(dispatcherSearchTerm) ||
        dispatcher.cellNumber?.includes(dispatcherSearchTerm)
    );

    const filteredDoctors = selectedSpecialty
        ? doctors.filter(doctor => doctor.Specialty === selectedSpecialty)
        : doctors;

    return (
        <Container maxWidth="lg" sx={{ 
            padding: { xs: '10px', sm: '20px' },
            paddingTop: { xs: '66px', sm: '80px' },
            width: '100%',
            maxWidth: '100%',
            minHeight: '100vh',
            backgroundColor: '#f5f5f5'
        }}>
            <Box sx={{ 
                mb: { xs: 2, sm: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 0 }
            }}>
                <Typography variant="h4" component="h1" sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    mb: { xs: 1, sm: 0 }
                }}>
                    Directory
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    {isAdmin && activeTab === 'responders' && (
                        <>
                            <Tooltip title="Toggle Admin Mode">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={adminMode}
                                            onChange={(e) => setAdminMode(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            fontSize: { xs: '0.8rem', sm: '1rem' }
                                        }}>
                                            <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                                            Admin Mode
                                        </Box>
                                    }
                                />
                            </Tooltip>
                            {adminMode && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => setOpenAddDialog(true)}
                                    sx={{ 
                                        ml: { xs: 0, sm: 2 },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                >
                                    Add New User
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                indicatorColor="primary" 
                textColor="primary" 
                centered
                sx={{ mb: 3 }}
            >
                <Tab label="Responders" value="responders" />
                <Tab label="Dispatchers" value="dispatchers" />
                <Tab label="Important Addresses" value="addresses" />
                <Tab label="Doctors" value="doctors" />
            </Tabs>

            {activeTab === 'responders' && (
                <>
                    <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by name, unit number, or phone..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                mb: 3,
                                '& .MuiInputBase-root': {
                                    height: { xs: '40px', sm: '56px' }
                                }
                            }}
                        />
                    </Box>

                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                        {filteredResponders.map((responder) => (
                            <Grid item xs={12} sm={6} md={4} key={responder.id}>
                                <Card sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: { xs: '8px', sm: '12px' },
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease',
                                    border: { xs: '1px solid #1976d2', sm: '2px solid #1976d2' },
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-4px)' },
                                        boxShadow: { xs: '0 2px 4px rgba(0, 0, 0, 0.1)', sm: '0 6px 12px rgba(0, 0, 0, 0.1)' }
                                    }
                                }}>
                                    <CardContent sx={{ 
                                        flexGrow: 1, 
                                        p: { xs: 2, sm: 3 },
                                        '&:last-child': { pb: { xs: 2, sm: 3 } }
                                    }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            mb: { xs: 1, sm: 2 },
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            textAlign: { xs: 'center', sm: 'left' }
                                        }}>
                                            <Avatar 
                                                sx={{ 
                                                    width: { xs: 50, sm: 60 }, 
                                                    height: { xs: 50, sm: 60 }, 
                                                    bgcolor: '#1976d2',
                                                    mb: { xs: 1, sm: 0 },
                                                    mr: { xs: 0, sm: 2 }
                                                }}
                                            >
                                                {responder.displayName?.charAt(0) || 'R'}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" component="div" sx={{
                                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                                }}>
                                                    {responder.displayName}
                                                </Typography>
                                                <Chip 
                                                    label={responder.certLevel || 'Unknown'} 
                                                    color={responder.certLevel === 'ALS' ? 'error' : 'primary'}
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                />
                                            </Box>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                gap: 1, 
                                                mt: { xs: 2, sm: 0 },
                                                justifyContent: { xs: 'center', sm: 'flex-end' }
                                            }}>
                                                {adminMode && (
                                                    <>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleOpenEditDialog(responder)}
                                                            sx={{ 
                                                                backgroundColor: 'primary.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'primary.dark'
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDelete(responder.id)}
                                                            sx={{ 
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                mb: 1,
                                                justifyContent: { xs: 'center', sm: 'flex-start' }
                                            }}>
                                                <BadgeIcon sx={{ mr: 1, color: '#666' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Unit: {responder.unitNumber || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: { xs: 1, sm: 0 }
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    justifyContent: { xs: 'center', sm: 'flex-start' }
                                                }}>
                                                    <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPhoneNumber(responder.cellNumber)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                    display: 'flex',
                                                    justifyContent: { xs: 'center', sm: 'flex-end' }
                                                }}>
                                                    <IconButton 
                                                        href={`tel:${responder.cellNumber}`}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <PhoneIcon color="primary" />
                                                    </IconButton>
                                                    <IconButton 
                                                        href={`sms:${responder.cellNumber}`}
                                                        size="small"
                                                    >
                                                        <TextsmsIcon color="primary" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {activeTab === 'dispatchers' && (
                <>
                    <Box sx={{ 
                        mb: { xs: 2, sm: 4 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search dispatchers..."
                            value={dispatcherSearchTerm}
                            onChange={handleDispatcherSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                mb: 3,
                                '& .MuiInputBase-root': {
                                    height: { xs: '40px', sm: '56px' }
                                }
                            }}
                        />
                        {isAdmin && (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                <Tooltip title="Toggle Admin Mode">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={adminMode}
                                                onChange={(e) => setAdminMode(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                fontSize: { xs: '0.8rem', sm: '1rem' }
                                            }}>
                                                <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                                                Admin Mode
                                            </Box>
                                        }
                                    />
                                </Tooltip>
                                {adminMode && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => {
                                            setEditForm({
                                                displayName: '',
                                                unitNumber: '',
                                                cellNumber: '',
                                                certLevel: 'dispatcher'
                                            });
                                            setOpenAddDialog(true);
                                        }}
                                        sx={{ 
                                            ml: { xs: 0, sm: 2 },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        Add New Dispatcher
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                        {loadingDispatchers ? (
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Grid>
                        ) : filteredDispatchers.length > 0 ? (
                            filteredDispatchers.map((dispatcher) => (
                                <Grid item xs={12} sm={6} md={4} key={dispatcher.id}>
                                    <Card sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: { xs: '8px', sm: '12px' },
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease',
                                        border: { xs: '1px solid #1976d2', sm: '2px solid #1976d2' },
                                        '&:hover': {
                                            transform: { xs: 'none', sm: 'translateY(-4px)' },
                                            boxShadow: { xs: '0 2px 4px rgba(0, 0, 0, 0.1)', sm: '0 6px 12px rgba(0, 0, 0, 0.1)' }
                                        }
                                    }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Avatar sx={{ 
                                                    width: 60, 
                                                    height: 60, 
                                                    bgcolor: '#1976d2',
                                                    mr: 2
                                                }}>
                                                    {dispatcher.displayName?.charAt(0) || 'D'}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6">{dispatcher.displayName}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Unit: {dispatcher.unitNumber || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                {adminMode && (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleOpenEditDialog(dispatcher)}
                                                            sx={{ 
                                                                backgroundColor: 'primary.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'primary.dark'
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDelete(dispatcher.id)}
                                                            sx={{ 
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPhoneNumber(dispatcher.cellNumber)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                    <IconButton href={`tel:${dispatcher.cellNumber}`} sx={{ mr: 1 }}>
                                                        <PhoneIcon color="primary" />
                                                    </IconButton>
                                                    <IconButton href={`sms:${dispatcher.cellNumber}`}>
                                                        <TextsmsIcon color="primary" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Typography align="center">No dispatchers found.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {activeTab === 'addresses' && (
                <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                    {loadingAddresses ? (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Grid>
                    ) : addressError ? (
                        <Grid item xs={12}>
                            <Typography color="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{addressError}</Typography>
                        </Grid>
                    ) : (
                        importantAddresses.map((address) => (
                            <Grid item xs={12} sm={6} md={4} key={address.id}>
                                <Card sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: { xs: '8px', sm: '12px' },
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease',
                                    border: { xs: '1px solid #1976d2', sm: '2px solid #1976d2' },
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-4px)' },
                                        boxShadow: { xs: '0 2px 4px rgba(0, 0, 0, 0.1)', sm: '0 6px 12px rgba(0, 0, 0, 0.1)' }
                                    }
                                }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {address.BuildingName || 'Unnamed Building'}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                {address.addressLine1}
                                            </Typography>
                                            {address.addressLine2 && (
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    {address.addressLine2}
                                                </Typography>
                                            )}
                                            <Box sx={{ mt: 2 }}>
                                                <Chip 
                                                    label={`Access Code: ${address.accessCode || 'N/A'}`}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            )}

            {activeTab === 'doctors' && (
                <>
                    <Box sx={{ mb: 3 }}>
                        <Autocomplete
                            options={specialties}
                            getOptionLabel={(option) => option}
                            onChange={(event, newValue) => setSelectedSpecialty(newValue)}
                            renderInput={(params) => <TextField {...params} label="Select Specialty" variant="outlined" />}
                            fullWidth
                        />
                    </Box>
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                        {loadingDoctors ? (
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Grid>
                        ) : filteredDoctors.length > 0 ? (
                            filteredDoctors.map((doctor) => (
                                <Grid item xs={12} sm={6} md={4} key={doctor.id}>
                                    <Card sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: { xs: '8px', sm: '12px' },
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease',
                                        border: { xs: '1px solid #1976d2', sm: '2px solid #1976d2' },
                                        '&:hover': {
                                            transform: { xs: 'none', sm: 'translateY(-4px)' },
                                            boxShadow: { xs: '0 2px 4px rgba(0, 0, 0, 0.1)', sm: '0 6px 12px rgba(0, 0, 0, 0.1)' }
                                        }
                                    }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Avatar sx={{ 
                                                    width: 60, 
                                                    height: 60, 
                                                    bgcolor: '#1976d2',
                                                    mr: 2
                                                }}>
                                                    {doctor.Name?.charAt(0) || 'D'}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h6">{doctor.Name}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {doctor.Specialty}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPhoneNumber(doctor.Phone)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                    <IconButton href={`tel:${doctor.Phone}`} sx={{ mr: 1 }}>
                                                        <PhoneIcon color="primary" />
                                                    </IconButton>
                                                    <IconButton href={`sms:${doctor.Phone}`}>
                                                        <TextsmsIcon color="primary" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Typography align="center">No doctors found.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {/* Edit Dialog */}
            <Dialog 
                open={openEditDialog} 
                onClose={handleCloseEditDialog} 
                maxWidth="sm" 
                fullWidth
                fullScreen={window.innerWidth < 600}
            >
                <DialogTitle sx={{ 
                    pb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                }}>
                    Edit Responder
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseEditDialog}
                        sx={{ 
                            position: 'absolute', 
                            right: 8, 
                            top: 8,
                            p: { xs: 1, sm: 2 }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ 
                    pt: { xs: 2, sm: 3 },
                    '& .MuiTextField-root': {
                        mb: { xs: 2, sm: 3 }
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: { xs: 2, sm: 3 }
                    }}>
                        <TextField
                            label="Name"
                            value={editForm.displayName}
                            onChange={(e) => setEditForm({...editForm, displayName: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Unit Number"
                            value={editForm.unitNumber}
                            onChange={(e) => setEditForm({...editForm, unitNumber: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Phone Number"
                            value={editForm.cellNumber}
                            onChange={(e) => setEditForm({...editForm, cellNumber: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Certification Level"
                            value={editForm.certLevel}
                            onChange={(e) => setEditForm({...editForm, certLevel: e.target.value})}
                            fullWidth
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ 
                    p: { xs: 2, sm: 3 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                }}>
                    <Button 
                        onClick={handleCloseEditDialog}
                        fullWidth={window.innerWidth < 600}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleEditSubmit} 
                        variant="contained" 
                        color="primary"
                        fullWidth={window.innerWidth < 600}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Dialog */}
            <Dialog 
                open={openAddDialog} 
                onClose={handleCloseAddDialog} 
                maxWidth="sm" 
                fullWidth
                fullScreen={window.innerWidth < 600}
            >
                <DialogTitle sx={{ 
                    pb: { xs: 1, sm: 2 },
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                }}>
                    Add New Responder
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseAddDialog}
                        sx={{ 
                            position: 'absolute', 
                            right: 8, 
                            top: 8,
                            p: { xs: 1, sm: 2 }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ 
                    pt: { xs: 2, sm: 3 },
                    '& .MuiTextField-root': {
                        mb: { xs: 2, sm: 3 }
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: { xs: 2, sm: 3 }
                    }}>
                        <TextField
                            label="Name"
                            value={editForm.displayName}
                            onChange={(e) => setEditForm({...editForm, displayName: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Unit Number"
                            value={editForm.unitNumber}
                            onChange={(e) => setEditForm({...editForm, unitNumber: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Phone Number"
                            value={editForm.cellNumber}
                            onChange={(e) => setEditForm({...editForm, cellNumber: e.target.value})}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Certification Level"
                            value={editForm.certLevel}
                            onChange={(e) => setEditForm({...editForm, certLevel: e.target.value})}
                            fullWidth
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ 
                    p: { xs: 2, sm: 3 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                }}>
                    <Button 
                        onClick={handleCloseAddDialog}
                        fullWidth={window.innerWidth < 600}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddSubmit} 
                        variant="contained" 
                        color="primary"
                        fullWidth={window.innerWidth < 600}
                    >
                        Add Responder
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{ 
                    vertical: 'bottom', 
                    horizontal: 'center' 
                }}
            >
                <Alert 
                    onClose={() => setSnackbar({...snackbar, open: false})} 
                    severity={snackbar.severity}
                    sx={{ 
                        width: '100%',
                        maxWidth: { xs: '90%', sm: '400px' }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Directory; 